import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MonthlyData } from "@/hooks/use-dashboard-analytics";
import { useValuesVisibility } from "@/hooks/use-values-visibility";

interface MonthlyBalanceChartProps {
  data: MonthlyData[];
  isLoading?: boolean;
  compact?: boolean;
}

const chartConfig = {
  income: {
    label: "Receitas",
    color: "hsl(var(--success))",
  },
  expense: {
    label: "Despesas",
    color: "hsl(var(--destructive))",
  },
};

export function MonthlyBalanceChart({ data, isLoading, compact }: MonthlyBalanceChartProps) {
  const { visible, mask } = useValuesVisibility();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Balanço Mensal</CardTitle>
          <CardDescription>Receitas vs Despesas</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Balanço Mensal</CardTitle>
        <CardDescription>Receitas vs Despesas (6 meses)</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className={compact ? "h-[180px] w-full" : "h-[280px] w-full"}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
              <XAxis 
                dataKey="monthLabel" 
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                className="fill-muted-foreground"
              />
              <YAxis 
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                className="fill-muted-foreground"
                tickFormatter={(value) => visible ? `${(value / 1000).toFixed(0)}k` : "***"}
              />
              <ChartTooltip 
                content={
                  <ChartTooltipContent 
                    formatter={(value) => 
                      mask(new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(Number(value)))
                    }
                  />
                }
              />
              <Bar 
                dataKey="income" 
                name="Receitas"
                fill="hsl(var(--success))" 
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="expense" 
                name="Despesas"
                fill="hsl(var(--destructive))" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
